/**
 * Floating menu rendered whenever a suggestion trigger (`/`, `@`, `!`,
 * `#`) is active in the day-note editor. Ported from Loop's
 * meeting-v2/SuggestionMenu.tsx, restyled onto the CRM's plain-CSS
 * tokens (day-note.css).
 *
 * Stateful only on `selected`; commits on click or via the suggestion
 * plugin's `onKeyDown(Enter)` path through the imperative ref.
 */
import { forwardRef, useImperativeHandle, useState } from "react";

export interface SuggestionItem {
    /** Stable key — cmd literal, contact id, target id, etc. */
    key: string;
    label: string;
    /** Helper text (cmd help, kind label). */
    hint?: string | null;
    /** Example line rendered under a command. */
    example?: string;
    /** "cmd" suppresses the initials badge (label already starts with /). */
    kind?: string;
}

export interface SuggestionMenuRef {
    onKeyDown: (event: KeyboardEvent) => boolean;
}

export const SuggestionMenu = forwardRef<
    SuggestionMenuRef,
    { label: string; items: SuggestionItem[]; onSelect: (index: number) => void }
>(function SuggestionMenu({ label, items, onSelect }, ref) {
    const [selected, setSelected] = useState(0);
    if (selected >= items.length && items.length > 0) setSelected(0);

    useImperativeHandle(ref, () => ({
        onKeyDown: (event: KeyboardEvent) => {
            if (items.length === 0) return false;
            if (event.key === "ArrowDown") {
                setSelected((s) => (s + 1) % items.length);
                return true;
            }
            if (event.key === "ArrowUp") {
                setSelected((s) => (s - 1 + items.length) % items.length);
                return true;
            }
            if (event.key === "Enter" || event.key === "Tab") {
                onSelect(selected);
                return true;
            }
            return false;
        },
    }), [items.length, selected, onSelect]);

    const isCmdMenu = items.length > 0 && items[0]!.kind === "cmd";

    return (
        <div className="loop-sugg-menu">
            <div className="loop-sugg-head">{label}</div>
            {items.length === 0 && <div className="loop-sugg-empty">no matches</div>}
            {items.map((it, idx) => {
                const initials = isCmdMenu
                    ? null
                    : it.label.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
                // Directory pools arrive ordered by kind — a heading marks
                // each change so `#` reads as Accounts / Leads / Events / Meetings.
                const groupHead = !isCmdMenu && it.hint && it.hint !== items[idx - 1]?.hint
                    ? <div className="loop-sugg-group">{it.hint}</div>
                    : null;
                return (
                    <div key={it.key}>
                        {groupHead}
                        <button
                            type="button"
                            // preventDefault keeps the editor focused/selection
                            // alive while the menu takes the click.
                            onMouseDown={(e) => { e.preventDefault(); onSelect(idx); }}
                            onMouseEnter={() => setSelected(idx)}
                            className={`loop-sugg-item${idx === selected ? " sel" : ""}${isCmdMenu ? " cmd" : ""}`}
                        >
                            {initials && <span className="loop-sugg-badge">{initials}</span>}
                            {isCmdMenu ? (
                                <span className="loop-sugg-cmd">
                                    <span className="loop-sugg-label mono">{it.label}</span>
                                    {it.hint && <span className="loop-sugg-help">{it.hint}</span>}
                                    {it.example && <span className="loop-sugg-example">{it.example}</span>}
                                </span>
                            ) : (
                                <span className="loop-sugg-label">{it.label}</span>
                            )}
                        </button>
                    </div>
                );
            })}
        </div>
    );
});
